import React from 'react';
import { Pencil, Copy, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import { formatCurrency } from '../../../utils/format';
import { renderCatalogIcon } from '../../../utils/renderCatalogIcon';
import type { ProductTemplate } from '../../../api/types';
import { MarginBar } from './MarginBar';
import { EditablePriceCell } from '../../../components/products/EditablePriceCell';
import type { ProductCardColumn } from '../../../components/products/ProductColumnToggle';

function marginPct(baseCost: number, basePrice: number): number {
  if (basePrice <= 0) return 0;
  return ((basePrice - baseCost) / basePrice) * 100;
}

const iconBtnSt: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: 'var(--color-text-muted)',
  padding: '0.25rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'color var(--transition-interactive, 200ms ease)',
};

interface ProductCardProps {
  template: ProductTemplate;
  hiddenColumns?: ProductCardColumn[];
  isExpanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onToggleActive: () => void;
  onSaveCost?: (value: number) => Promise<void>;
  onSavePrice?: (value: number) => Promise<void>;
}

export function ProductCard({
  template,
  hiddenColumns = [],
  isExpanded,
  onToggle,
  onEdit,
  onDuplicate,
  onDelete,
  onToggleActive,
  onSaveCost,
  onSavePrice,
}: ProductCardProps) {
  const margin = marginPct(template.baseCost, template.basePrice);
  const marginAbs = template.basePrice - template.baseCost;

  return (
    <div
      style={{
        background: 'var(--glass-bg-card)',
        border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: 'var(--radius-card)',
        overflow: 'hidden',
        transition: 'all 0.2s ease',
        opacity: template.isActive ? 1 : 0.55,
      }}
    >
      {/* Header / Collapsed View - Always Visible */}
      <button
        onClick={onToggle}
        style={{
          width: '100%',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '0.75rem 1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          color: 'var(--color-text)',
          fontSize: 'var(--text-sm)',
          transition: 'background-color var(--transition-interactive, 200ms ease)',
          textAlign: 'left',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--glass-bg-card)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        {/* Category Emoji */}
        {!hiddenColumns.includes('categoria') && (
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 'var(--radius-interactive)',
              background: 'var(--glass-bg-card)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              flexShrink: 0,
            }}
          >
            {renderCatalogIcon(template.icon, 16)}
          </div>
        )}

        {/* Name */}
        <span style={{ fontWeight: 600, flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {template.name}
        </span>

        {/* Price */}
        {!hiddenColumns.includes('prezzo') && (
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
            {formatCurrency(template.basePrice)}
          </span>
        )}

        {/* Margin % */}
        {!hiddenColumns.includes('costo') && (
          <span
            style={{
              fontSize: 'var(--text-xs)',
              fontWeight: 600,
              color: margin >= 50 ? 'var(--color-success)' : margin >= 20 ? 'var(--color-warning)' : 'var(--color-error)',
              whiteSpace: 'nowrap',
            }}
          >
            {margin.toFixed(0)}%
          </span>
        )}

        {/* Status Badge */}
        {!hiddenColumns.includes('stato') && !template.isActive && (
          <span
            style={{
              fontSize: '9px',
              fontWeight: 700,
              textTransform: 'uppercase',
              color: 'var(--color-text-faint)',
              background: 'var(--glass-bg-card)',
              padding: '0.125rem 0.375rem',
              borderRadius: 'var(--radius-interactive)',
              border: '1px solid rgba(255,255,255,0.10)',
              whiteSpace: 'nowrap',
            }}
          >
            Inattivo
          </span>
        )}
      </button>

      {/* Expanded Details */}
      <div
        style={{
          maxHeight: isExpanded ? '1000px' : 0,
          overflow: 'hidden',
          transition: 'max-height 0.3s ease',
          borderTop: isExpanded ? '1px solid rgba(255,255,255,0.10)' : 'none',
        }}
      >
        <div style={{ padding: '1rem' }}>
          {/* Category & Description */}
          {!hiddenColumns.includes('categoria') && (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
              <span
                style={{
                  fontSize: '10px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  color: 'var(--color-primary)',
                  background: 'oklch(from var(--color-primary) l c h / 0.12)',
                  padding: '0.0625rem 0.375rem',
                  borderRadius: 'var(--radius-interactive)',
                }}
              >
                {template.category.charAt(0).toUpperCase() + template.category.slice(1)}
              </span>
              {template.attributes.length > 0 && (
                <span style={{ fontSize: '10px', color: 'var(--color-text-faint)' }}>
                  {template.attributes.length} attr.
                </span>
              )}
            </div>
          )}

          {!hiddenColumns.includes('descrizione') && template.description && (
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginBottom: '0.75rem' }}>
              {template.description}
            </p>
          )}

          {/* cost/price/margin Row */}
          {!hiddenColumns.includes('costo') && (
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <div>
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>Costo </span>
                {onSaveCost ? (
                  <EditablePriceCell value={template.baseCost} onSave={onSaveCost} label="Costo" />
                ) : (
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 500 }}>
                    {formatCurrency(template.baseCost)}
                  </span>
                )}
              </div>
              <div>
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>Margine </span>
                <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: margin >= 0 ? 'var(--color-success)' : 'var(--color-error)' }}>
                  {formatCurrency(marginAbs)}
                </span>
              </div>
              {!hiddenColumns.includes('unita') && template.unit && (
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>/ {template.unit}</span>
              )}
            </div>
          )}

          {/* Visual Margin Bar */}
          {!hiddenColumns.includes('costo') && (
            <div style={{ marginBottom: '0.75rem' }}>
              <MarginBar margin={margin} />
            </div>
          )}

          {/* Custom Attributes */}
          {template.attributes.length > 0 && (
            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--color-text-faint)', marginBottom: '0.375rem', textTransform: 'uppercase' }}>
                Attributi
              </div>
              <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
                {template.attributes.map((attr) => (
                  <div
                    key={attr.id}
                    style={{
                      fontSize: '11px',
                      background: 'var(--glass-bg-card)',
                      border: '1px solid rgba(255,255,255,0.10)',
                      borderRadius: 'var(--radius-interactive)',
                      padding: '0.25rem 0.5rem',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {attr.key}{attr.defaultValue ? `: ${attr.defaultValue}` : ''}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions Row */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              style={{
                ...iconBtnSt,
                background: 'var(--glass-bg-card)',
                color: 'var(--color-text)',
                padding: '0.375rem 0.75rem',
                fontSize: 'var(--text-xs)',
                fontWeight: 600,
                borderRadius: 'var(--radius-interactive)',
                border: '1px solid rgba(255,255,255,0.10)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-primary)';
                e.currentTarget.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--glass-bg-card)';
                e.currentTarget.style.color = 'var(--color-text)';
              }}
            >
              <Pencil size={12} style={{ marginRight: '0.25rem' }} />
              Modifica
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                onDuplicate();
              }}
              style={{
                ...iconBtnSt,
                background: 'var(--glass-bg-card)',
                color: 'var(--color-text)',
                padding: '0.375rem 0.75rem',
                fontSize: 'var(--text-xs)',
                fontWeight: 600,
                borderRadius: 'var(--radius-interactive)',
                border: '1px solid rgba(255,255,255,0.10)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-primary)';
                e.currentTarget.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--glass-bg-card)';
                e.currentTarget.style.color = 'var(--color-text)';
              }}
            >
              <Copy size={12} style={{ marginRight: '0.25rem' }} />
              Duplica
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleActive();
              }}
              style={{
                ...iconBtnSt,
                background: 'none',
                border: 'none',
                padding: '0.375rem 0.75rem',
                fontSize: 'var(--text-xs)',
                fontWeight: 600,
                borderRadius: 'var(--radius-interactive)',
              }}
              title={template.isActive ? 'Disattiva' : 'Attiva'}
            >
              {template.isActive
                ? <ToggleRight size={16} style={{ color: 'var(--color-success)', marginRight: '0.25rem' }} />
                : <ToggleLeft size={16} style={{ marginRight: '0.25rem' }} />}
              {template.isActive ? 'Attivo' : 'Inattivo'}
            </button>
          </div>

          {/* Delete Button - Separate, Red */}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (confirm('Eliminare questo prodotto?')) {
                  onDelete();
                }
              }}
              style={{
                ...iconBtnSt,
                color: 'var(--color-error)',
                background: 'none',
                border: 'none',
                padding: '0.375rem 0.75rem',
                fontSize: 'var(--text-xs)',
                fontWeight: 600,
                borderRadius: 'var(--radius-interactive)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'oklch(from var(--color-error) l c h / 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <Trash2 size={12} style={{ marginRight: '0.25rem' }} />
              Elimina
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
